import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLock } from 'react-icons/fi';
import { Badge } from '../common/Badge';
import { formatPercent, formatCurrency, formatGameTimeLabel } from '../../utils/formatters';
import { getTeamLogo } from '../../utils/teamLogos';
import { Fire, Rocket } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';

export function MarketCard({ market, prediction }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [pointsBalance, setPointsBalance] = useState(null);
  const [pointsPrice, setPointsPrice] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsPurchaseLoading, setPointsPurchaseLoading] = useState(false);
  const [pointsError, setPointsError] = useState('');

  const { teamA, teamB, priceA, priceB, volume, status, recentFormA, recentFormB, gameTime, aiProbA, aiProbB, postponed } = market;
  const ev = prediction?.ev || prediction?.edge || 0;
  const tierName = user?.tier_name || user?.membership_tier || 'free';
  const isAiLocked = tierName === 'free' && !market?.pointsUnlocked;
  const aiPickLabel = aiProbA > aiProbB ? teamA : teamB;
  const aiPickDisplay = isAiLocked ? '--' : aiPickLabel;
  const aiPickTitle = isAiLocked ? '' : aiPickLabel;
  const aiProbADisplay = isAiLocked ? '--' : formatPercent(aiProbA);
  const aiProbBDisplay = isAiLocked ? '--' : formatPercent(aiProbB);

  const renderForm = (form) => {
    if (!form || form.length === 0) return null;
    return (
      <div className="flex gap-px">
        {form.slice(0, 5).map((result, i) => (
          <span
            key={i}
            className={`w-3.5 h-3.5 flex items-center justify-center text-[9px] font-bold rounded-sm ${result === 'W' ? 'bg-positive/20 text-positive' : 'bg-negative/20 text-negative'
              }`}
          >
            {result}
          </span>
        ))}
      </div>
    );
  };

  const formatGameTime = (time) =>
    formatGameTimeLabel(time, {
      todayLabel: t('market.today'),
      tomorrowLabel: t('market.tomorrow'),
    });

  const isHot = volume > 100000;
  const hasEdge = ev > 3;

  const handleAiUnlock = (event) => {
    if (!isAiLocked) return;
    event.preventDefault();
    event.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }
    setUnlockModalOpen(true);
  };

  const renderAiLockOverlay = ({ showLabel = false, className = '' } = {}) => {
    if (!isAiLocked) return null;
    return (
      <button
        type="button"
        onClick={handleAiUnlock}
        className={`absolute inset-0 flex items-center justify-center gap-1 rounded-md bg-slate-950/60 text-[10px] font-semibold text-amber-100 backdrop-blur-sm ${className}`}
      >
        <FiLock size={12} />
        {showLabel && <span>{t('membership.clickToView', '点击查看')}</span>}
      </button>
    );
  };

  const loadPointsInfo = useCallback(async () => {
    if (!user || !unlockModalOpen) return;
    setPointsLoading(true);
    setPointsError('');
    try {
      const [balanceRes, productsRes] = await Promise.all([
        api.getPointsBalance(),
        api.getPointsProducts(),
      ]);
      const balance = balanceRes?.data?.balance ?? 0;
      const products = productsRes?.data?.products || [];
      const matchProduct = products.find((p) => p.product_code === 'match_access');
      setPointsBalance(balance);
      setPointsPrice(matchProduct?.price_points ?? null);
    } catch (err) {
      setPointsError(err?.message || t('points.unlockFailed', '解锁失败'));
    } finally {
      setPointsLoading(false);
    }
  }, [t, unlockModalOpen, user]);

  useEffect(() => {
    if (unlockModalOpen) {
      loadPointsInfo();
    }
  }, [unlockModalOpen, loadPointsInfo]);

  const handlePointsUnlock = useCallback(async () => {
    if (!market?.id || pointsPurchaseLoading) return;
    setPointsPurchaseLoading(true);
    setPointsError('');
    try {
      const result = await api.createPointsPurchase(
        'match_access',
        'market',
        market.id
      );
      if (result.code === 'SUCCESS') {
        setUnlockModalOpen(false);
        navigate(`/market/${market.id}`);
      } else if (result.code === 'INSUFFICIENT_POINTS') {
        setPointsError(t('points.insufficient', '点数不足'));
      } else {
        setPointsError(result.msg || t('points.unlockFailed', '解锁失败'));
      }
    } catch (err) {
      setPointsError(err?.message || t('points.unlockFailed', '解锁失败'));
    } finally {
      setPointsPurchaseLoading(false);
    }
  }, [market?.id, navigate, pointsPurchaseLoading, t]);

  const handleUpgrade = useCallback(() => {
    setUnlockModalOpen(false);
    window.dispatchEvent(
      new CustomEvent('membership-required', {
        detail: { currentTier: tierName },
      })
    );
  }, [tierName]);

  return (
    <>
      <div
        onClick={() => navigate(`/market/${market.id}`)}
        className="glass-card p-3 hover:scale-[1.02] active:scale-[0.98] cursor-pointer group relative overflow-hidden"
      >
      {/* Glow Effect on Hover */}
      <div className="absolute inset-0 bg-primary-glow opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-xl" />

      {/* Header: Time + Volume + Status */}
      <div className="flex items-center justify-between mb-3 text-[10px] sm:text-xs font-medium">
        <div className="flex items-center gap-2">
          {/* 比赛时间：推迟的比赛如果原时间已过则显示 TBD，否则显示时间 */}
          <span className="text-gray-400 group-hover:text-gray-300 transition-colors">
            {(() => {
              if (postponed) {
                // 推迟的比赛：如果时间已过，显示 TBD；否则显示新时间
                if (!gameTime) return 'TBD';
                const gameDate = new Date(gameTime);
                const isPast = gameDate.getTime() < Date.now();
                return isPast ? 'TBD' : formatGameTime(gameTime);
              }
              return gameTime ? formatGameTime(gameTime) : '';
            })()}
          </span>
          {/* 状态标签：推迟的比赛用 POSTPONED 替换 UPCOMING */}
          {postponed ? (
            <Badge variant="orange" className="px-1.5 py-0 animate-pulse">
              POSTPONED
            </Badge>
          ) : (
            <Badge variant={status === 'live' ? 'positive' : status === 'upcoming' ? 'neutral' : 'gray'} className="px-1.5 py-0">
              {status === 'live' ? 'LIVE' : status?.toUpperCase()}
            </Badge>
          )}
        </div>
        <span className="text-gray-500 group-hover:text-gray-400 transition-colors">
          Vol: <span className="text-gray-300">{formatCurrency(volume)}</span>
        </span>
      </div>

      {/* Team A Row */}
      <div className="flex items-center justify-between mb-2 relative z-10">
        <div className="flex items-center gap-2 flex-1 min-w-0 mr-1">
          <div className="w-8 h-8 rounded-full bg-surface-highlight p-1 shadow-lg border border-white/5 shrink-0 flex items-center justify-center">
            <img
              src={getTeamLogo(teamA)}
              alt={teamA}
              className="w-full h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <span className="font-bold text-sm text-gray-100 truncate group-hover:text-white transition-colors tracking-tight" title={teamA}>{teamA}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative flex items-center justify-end min-w-[52px]">
            <span
              className={`text-base sm:text-lg font-bold font-mono tracking-tight ${
                isAiLocked ? 'text-gray-600' : aiProbA > 0.5 ? 'text-positive' : 'text-gray-300'
              }`}
            >
              {aiProbADisplay}
            </span>
            {renderAiLockOverlay({ className: 'rounded-sm' })}
          </div>
          <div className="flex gap-px">
            {recentFormA?.slice(0, 5).map((result, i) => (
              <span
                key={i}
                className={`w-4 h-4 flex items-center justify-center text-[10px] font-bold rounded-sm ${result === 'W' ? 'bg-positive/20 text-positive' : 'bg-negative/20 text-negative'
                  }`}
              >
                {result}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Team B Row */}
      <div className="flex items-center justify-between mb-3 relative z-10">
        <div className="flex items-center gap-2 flex-1 min-w-0 mr-1">
          <div className="w-8 h-8 rounded-full bg-surface-highlight p-1 shadow-lg border border-white/5 shrink-0 flex items-center justify-center">
            <img
              src={getTeamLogo(teamB)}
              alt={teamB}
              className="w-full h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <span className="font-bold text-sm text-gray-100 truncate group-hover:text-white transition-colors tracking-tight" title={teamB}>{teamB}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative flex items-center justify-end min-w-[52px]">
            <span
              className={`text-base sm:text-lg font-bold font-mono tracking-tight ${
                isAiLocked ? 'text-gray-600' : aiProbB > 0.5 ? 'text-positive' : 'text-gray-300'
              }`}
            >
              {aiProbBDisplay}
            </span>
            {renderAiLockOverlay({ className: 'rounded-sm' })}
          </div>
          <div className="flex gap-px">
            {recentFormB?.slice(0, 5).map((result, i) => (
              <span
                key={i}
                className={`w-4 h-4 flex items-center justify-center text-[10px] font-bold rounded-sm ${result === 'W' ? 'bg-positive/20 text-positive' : 'bg-negative/20 text-negative'
                  }`}
              >
                {result}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Footer: Poly Odds + AI Prediction */}
      <div className="pt-3 border-t border-white/5 relative z-10">
        <div className="flex items-center justify-between">
          {/* Poly Odds (Price) */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Poly {t('market.odds')}</span>
            <div className="flex items-center h-6 px-2 rounded-md bg-white/[0.03] border border-white/5 gap-2 text-xs font-mono font-bold">
              <span className="text-positive">{formatPercent(priceA)}</span>
              <span className="text-slate-600">/</span>
              <span className="text-negative">{formatPercent(priceB)}</span>
            </div>
          </div>

          {/* Right Side: AI Prediction + Hot Badge */}
          <div className="flex items-center gap-3 justify-end">
            {/* AI Analysis - Always show, using aiProbA/B which fallback to Poly odds */}
            <div className="flex flex-col items-end text-right">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-accent-soft to-accent bg-clip-text text-transparent">AI {t('market.prediction')}</span>
              </div>
              <div className="relative max-w-[100px] sm:max-w-[120px] min-w-[84px] sm:min-w-[100px]">
                <span
                  className={`text-xs font-bold truncate block drop-shadow-sm ${
                    isAiLocked ? 'text-gray-600' : 'text-slate-200'
                  }`}
                  title={aiPickTitle}
                >
                  {aiPickDisplay}
                </span>
                {renderAiLockOverlay({ showLabel: true })}
              </div>
            </div>

            {/* Hot Badge */}
            {isHot && (
              <Badge variant="orange" className="text-[10px] px-1.5 py-0.5 shadow-[0_0_10px_rgba(234,179,8,0.2)] flex items-center gap-1">
                <Fire size={12} weight="duotone" /> {t('market.hot')}
              </Badge>
            )}

            {/* Edge Badge fallback */}
            {hasEdge && !isHot && (
              <Badge variant="positive" className="text-[10px] px-1.5 py-0.5 shadow-[0_0_10px_rgba(34,197,94,0.2)] flex items-center gap-1">
                <Rocket size={12} weight="duotone" /> +{ev.toFixed(1)}%
              </Badge>
            )}
          </div>
        </div>
      </div>
      </div>

      {unlockModalOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-2xl border border-glass-border bg-surface/95 p-5 text-slate-100 shadow-[0_30px_70px_rgba(0,0,0,0.45)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
                  {t('points.balance', '点数')}
                </div>
                <div className="mt-2 text-lg font-semibold text-white">
                  {t('points.choiceTitle', '解锁本场比赛')}
                </div>
                <div className="mt-1 text-sm text-slate-300">
                  {t('points.choiceDesc', '使用点数解锁本场比赛，或升级会员免费使用')}
                </div>
              </div>
              <button
                className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white"
                onClick={() => setUnlockModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="mt-4 rounded-xl border border-glass-border bg-surface/70 p-4">
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>{t('points.balance', '点数余额')}</span>
                <span className="text-white">
                  {pointsLoading ? '...' : (pointsBalance ?? '-')}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                <span>{t('points.price', '解锁价格')}</span>
                <span className="text-white">
                  {pointsLoading ? '...' : (pointsPrice ?? '-')}
                </span>
              </div>
              <button
                onClick={handlePointsUnlock}
                disabled={pointsLoading || pointsPurchaseLoading || pointsPrice == null}
                className="mt-4 w-full rounded-xl bg-gradient-to-r from-accent-strong to-accent px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {pointsPurchaseLoading
                  ? t('points.unlocking', '解锁中...')
                  : t('points.unlockCta', `使用${pointsPrice ?? '--'}点解锁`, {
                      points: pointsPrice ?? '--',
                    })}
              </button>
            </div>

            <div className="mt-3 rounded-xl border border-glass-border bg-surface/70 p-4">
              <div className="text-xs text-slate-400">
                {t('points.choiceUpgradeHint', '升级会员即可免费查看')}
              </div>
              <button
                onClick={handleUpgrade}
                className="mt-3 w-full rounded-xl border border-glass-border px-4 py-2 text-sm font-semibold text-slate-100 hover:border-white/20 hover:bg-white/5"
              >
                {t('membership.viewPlans', '查看会员方案')}
              </button>
            </div>

            {pointsError && (
              <div className="mt-3 text-xs text-negative">{pointsError}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
